import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Box, Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { defaultVariables } from "../../../config";
import SavedCardsSection from "../wallet/components/SavedCardsSection";
import { paymentMethodAddErrorMessage, validateCardForStore } from "../wallet/cardValidation";

const authHeaders = () => ({
    Accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("ACT")}`,
});

function AccountSavedCardsBlock() {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [cards, setCards] = useState([]);
    const [addOpen, setAddOpen] = useState(false);
    const [addLoading, setAddLoading] = useState(false);
    const [defaultUpdatingId, setDefaultUpdatingId] = useState(null);
    const [form, setForm] = useState({
        holder_name: "",
        card_number: "",
        exp_month: "",
        exp_year: "",
        label: "",
    });

    const loadCards = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${defaultVariables.url}/payment-methods`, { headers: authHeaders() });
            setCards(Array.isArray(data) ? data : []);
        } catch (e) {
            enqueueSnackbar(t("paramPayLoadError"), { variant: "error" });
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar, t]);

    useEffect(() => {
        loadCards();
    }, [loadCards]);

    const refreshCardsOnly = useCallback(async () => {
        try {
            const { data } = await axios.get(`${defaultVariables.url}/payment-methods`, { headers: authHeaders() });
            setCards(Array.isArray(data) ? data : []);
        } catch (e) {
            enqueueSnackbar(t("paramPayLoadError"), { variant: "error" });
        }
    }, [enqueueSnackbar, t]);

    const setCardDefault = async (id, isDefault) => {
        setDefaultUpdatingId(id);
        try {
            await axios.patch(
                `${defaultVariables.url}/payment-methods/${id}`,
                { is_default: isDefault },
                { headers: { ...authHeaders(), "Content-Type": "application/json" } },
            );
            setCards((prev) =>
                isDefault
                    ? prev.map((c) => ({ ...c, is_default: c.id === id }))
                    : prev.map((c) => (c.id === id ? { ...c, is_default: false } : c)),
            );
        } catch (e) {
            enqueueSnackbar(t("paramPayCardDefaultFailed"), { variant: "error" });
        } finally {
            setDefaultUpdatingId(null);
        }
    };

    const removeCard = async (id) => {
        try {
            await axios.delete(`${defaultVariables.url}/payment-methods/${id}`, { headers: authHeaders() });
            enqueueSnackbar(t("paramPayCardRemoved"), { variant: "success" });
            setCards((prev) => prev.filter((c) => c.id !== id));
        } catch (e) {
            enqueueSnackbar(t("paramPayCardRemoveFailed"), { variant: "error" });
        }
    };

    const addCard = async (e) => {
        e.preventDefault();
        const localErr = validateCardForStore(form);
        if (localErr) {
            enqueueSnackbar(t(`paramPayCardError_${localErr}`), { variant: "error" });
            return;
        }
        setAddLoading(true);
        try {
            const res = await axios.post(`${defaultVariables.url}/payment-methods`, form, {
                headers: { ...authHeaders(), "Content-Type": "application/json" },
            });
            const newCard = res.data;
            enqueueSnackbar(t("paramPayCardAdded"), { variant: "success" });
            setAddOpen(false);
            setForm({
                holder_name: "",
                card_number: "",
                exp_month: "",
                exp_year: "",
                label: "",
            });
            if (newCard && newCard.id != null) {
                setCards((prev) => [newCard, ...prev.filter((c) => c.id !== newCard.id)]);
            } else {
                refreshCardsOnly();
            }
        } catch (err) {
            enqueueSnackbar(paymentMethodAddErrorMessage(err, t), { variant: "error" });
        } finally {
            setAddLoading(false);
        }
    };

    if (loading) {
        return (
            <Box className="page-color-layout" width="100%">
                <Grid container spacing={2} sx={{ width: "100%" }}>
                    {[0, 1, 2].map((key) => (
                        <Grid key={key} size={{ xs: 12 }}>
                            <Skeleton
                                variant="rectangular"
                                width="100%"
                                height={55}
                                animation="wave"
                                sx={{ borderRadius: "12px" }}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    return (
        <Box className="page-color-layout" width="100%">
            <SavedCardsSection
                selectable={false}
                selectedId={null}
                cards={cards}
                addOpen={addOpen}
                setAddOpen={setAddOpen}
                form={form}
                setForm={setForm}
                addLoading={addLoading}
                onAddSubmit={addCard}
                onRemoveCard={removeCard}
                onDefaultChange={setCardDefault}
                defaultUpdatingId={defaultUpdatingId}
                showSectionHeading={false}
                emptyMessageKey="myCardsEmpty"
                showAcceptedBrandsStrip
            />
        </Box>
    );
}

export default AccountSavedCardsBlock;
